import { useState } from "react";

import {
  datum, eur, jahresabrechnungPdf, useJahresabrechnung, useJahresabrechnungFestschreiben,
  useJahresabrechnungSenden,
} from "../../hooks/useVertraege";
import type { VertragDetail } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";
import { Zeile } from "./teile";
import "../../styles/jahresabrechnung.css";

/**
 * Jahresabrechnung — die Abschlussrate eines Vertragsjahres.
 *
 * Benjamin, 26.10.2026: „regelmäßig die gleiche Rate, und zum Schluss eine
 * Ausgleichszahlung, je nachdem was mehr oder weniger geleistet wurde." Die
 * Raten sind Vorauszahlungen; hier stehen ihnen die erbrachten Einsätze des
 * Jahres gegenüber — höchstens so viele, wie im Leistungsverzeichnis stehen.
 * Eine Nachzahlung zieht die nächste Einzugsdatei mit ein, ein Guthaben wird
 * überwiesen. Festgeschrieben wird von Hand, wenn alle Einsätze des Jahres
 * eine Akte haben (Dezember-Einsätze werden oft erst im Januar dokumentiert).
 */
export function JahresabrechnungBlock({ v }: { v: VertragDetail }) {
  const heute = new Date();
  const beginnJahr = v.beginn ? Number(v.beginn.slice(0, 4)) : heute.getFullYear();
  // Im Januar und Februar geht es meist ums Vorjahr.
  const vorschlag = heute.getMonth() < 2 ? heute.getFullYear() - 1 : heute.getFullYear();
  const jahre = [heute.getFullYear() - 1, heute.getFullYear()].filter(j => j >= beginnJahr);
  const [jahr, setJahr] = useState(Math.max(vorschlag, beginnJahr));
  const { data: s, error } = useJahresabrechnung(v.id, jahr);
  const festschreiben = useJahresabrechnungFestschreiben(v.id, jahr);
  const senden = useJahresabrechnungSenden(v.id, jahr);
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");

  if (error) return <div className="pf-card ja-fehler">{fehlertext(error)}</div>;
  if (!s) return null;

  const brutto = Number(s.differenz_brutto ?? 0);
  const jahrVorbei = jahr < heute.getFullYear();
  const erbracht = s.zeilen.reduce((n, z) => n + z.erbracht, 0);
  const geplant = s.zeilen.reduce((n, z) => n + z.geplant, 0);
  const ueber = s.zeilen.reduce((n, z) => n + (z.ueber_plan ?? 0), 0);

  return (
    <div className={"pf-card ja-karte is-" + s.richtung}>
      <div className="ja-kopf">
        <span className="ja-titel">Jahresabrechnung {jahr}</span>
        {jahre.length > 1 && (
          <select className="pf-input is-tight ja-jahr" value={jahr} aria-label="Abrechnungsjahr"
                  onChange={e => { setJahr(Number(e.target.value)); setMeldung(""); setFehler(""); }}>
            {jahre.map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        )}
      </div>
      {/* Verbraucher zahlen brutto — dann auch brutto vergleichen, sonst steht
          netto gegen netto und darunter ein Bruttoausgleich. */}
      {s.leistungen_brutto ? (
        <>
          <Zeile l={`Erbrachte Einsätze (${erbracht} von ${geplant})`} r={`${eur(s.leistungen_brutto)} brutto`} />
          {Number(s.mehraufwand_brutto ?? 0) > 0 && (
            <Zeile l={`Zusatzleistungen (${s.mehraufwand_im_ausgleich?.length ?? 0})`}
                   r={`${eur(s.mehraufwand_brutto ?? 0)} brutto`} />
          )}
          <Zeile l="Vorauszahlungen (Raten)" r={`${eur(s.berechnet_brutto ?? 0)} brutto`} />
        </>
      ) : (
        <>
          <Zeile l={`Erbrachte Einsätze (${erbracht} von ${geplant})`} r={`${eur(s.leistungen_netto)} netto`} />
          {Number(s.mehraufwand_netto ?? 0) > 0 && (
            <Zeile l={`Zusatzleistungen (${s.mehraufwand_im_ausgleich?.length ?? 0})`}
                   r={`${eur(s.mehraufwand_netto ?? 0)} netto`} />
          )}
          <Zeile l="Vorauszahlungen (Raten)" r={`${eur(s.berechnet_netto)} netto`} />
        </>
      )}
      <Zeile l={s.richtung === "gutschrift" ? "Guthaben des Kunden" : s.richtung === "nachzahlung" ? "Nachzahlung" : "Ausgleich"}
             r={`${eur(Math.abs(brutto))} brutto`} strong />
      {s.ausgleich && <div className="ja-text">{s.ausgleich}</div>}
      {!jahrVorbei && (
        <div className="ja-klein">
          Das Jahr läuft noch — das ist der Stand heute. Festgeschrieben wird nach dem
          31.12., wenn alle Einsätze eine Akte haben.
        </div>
      )}
      {ueber > 0 && (
        <div className="ja-klein">
          {ueber} {ueber === 1 ? "Einsatz liegt" : "Einsätze liegen"} über dem Plan und
          {ueber === 1 ? " wird" : " werden"} nicht berechnet. Zusätzliches läuft als Mehraufwand.
        </div>
      )}

      <details className="ja-details">
        <summary>Was gerechnet wurde</summary>
        {s.zeilen.map(z => (
          <div key={z.leistung_id} className={"ja-zeile" + (z.erbracht > 0 ? "" : " is-leer")}>
            <span>{z.titel} <span className="ja-zeile-meta">· {z.erbracht} von {z.geplant}</span></span>
            <span className="ja-zeile-betrag">{eur(z.betrag_netto)}</span>
          </div>
        ))}
      </details>

      <div className="ja-knoepfe">
        <a className="pf-btn is-ghost" href={jahresabrechnungPdf(v.id, jahr)} target="_blank" rel="noreferrer">
          {s.festgeschrieben?.versendet_am ? "Abrechnung als PDF" : "PDF-Vorschau"}
        </a>
      </div>

      {s.festgeschrieben ? (
        <div className="ja-text">
          <strong>Festgeschrieben</strong> als Rechnung {s.festgeschrieben.rechnung_nummer || s.festgeschrieben.periode}
          {s.festgeschrieben.versendet_am
            ? ` — am ${datum(s.festgeschrieben.versendet_am)} an den Kunden gesendet`
              + (s.festgeschrieben.faellig_am ? `, Einzug am ${datum(s.festgeschrieben.faellig_am)}.` : ".")
            : "."}
          {!s.festgeschrieben.versendet_am && (
            <div>
              <button type="button" className="pf-btn ja-knopf" disabled={senden.isPending}
                onClick={async () => {
                  setFehler(""); setMeldung("");
                  try {
                    await senden.mutateAsync();
                    setMeldung("Gesendet. Die Mail enthält den Link zur Abrechnung in der Kundenakte.");
                  } catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
                }}>
                {senden.isPending ? "Sende…" : "An den Kunden senden"}
              </button>
            </div>
          )}
        </div>
      ) : jahrVorbei && s.richtung !== "ausgeglichen" ? (
        <button type="button" className="pf-btn ja-knopf" disabled={festschreiben.isPending}
          onClick={async () => {
            setFehler(""); setMeldung("");
            try {
              await festschreiben.mutateAsync();
              setMeldung("Festgeschrieben. Jetzt an den Kunden senden — die Abrechnung kündigt den Einzug an.");
            } catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
          }}>
          {festschreiben.isPending ? "Schreibe fest…" : `Jahresabrechnung ${jahr} festschreiben`}
        </button>
      ) : null}
      {meldung && <div className="ja-klein">{meldung}</div>}
      {fehler && <div className="ja-fehler">{fehler}</div>}
    </div>
  );
}
